import * as React from "react";
import { Head, router } from "@inertiajs/react";
import { GearItem } from "@/types/generated";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import AuthLayout from "@/layouts/AuthLayout";
import { InertiaPageComponent } from "@/types";
import {
  Settings2,
  CircleDot,
  Gamepad2,
  Glasses,
  Gauge,
  Plus,
  Edit,
  Archive
} from "lucide-react";

interface GearIndexProps {
  gear_items: GearItem[];
  gear_types: Array<{ value: string; label: string }>;
}

const GEAR_TYPE_ICONS: Record<string, React.ReactNode> = {
  wheel_base: <Settings2 className="w-5 h-5" />,
  steering_wheel: <CircleDot className="w-5 h-5" />,
  pedals: <Gauge className="w-5 h-5" />,
  shifter: <Gamepad2 className="w-5 h-5" />,
  vr_headset: <Glasses className="w-5 h-5" />,
  other: <Settings2 className="w-5 h-5" />,
};

const GearIndex: InertiaPageComponent<GearIndexProps> = ({
  gear_items,
  gear_types,
}) => {
  // Group gear by type
  const gearByType = React.useMemo(() => {
    const grouped: Record<string, GearItem[]> = {};
    gear_items.forEach((item) => {
      if (!grouped[item.gear_type]) {
        grouped[item.gear_type] = [];
      }
      grouped[item.gear_type].push(item);
    });
    return grouped;
  }, [gear_items]);

  const handleRetire = (uuid: string) => {
    if (confirm("Are you sure you want to retire this gear?")) {
      router.patch(`/garage/${uuid}/retire`);
    }
  };

  const handleSetPrimary = (uuid: string) => {
    router.patch(`/garage/${uuid}/set_primary`);
  };

  const formatLastUsed = (dateString: string | null) => {
    if (!dateString) return "Never used";

    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return `${Math.floor(diffDays / 30)} months ago`;
  };

  return (
    <>
      <Head title="My Garage - Timing Tower" />
      <main className="px-4 sm:px-6 lg:px-8 py-4">
        <div className="max-w-6xl mx-auto">
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 tracking-tight mb-1">
                My Garage
              </h2>
              <p className="text-gray-600 font-medium">
                Manage your sim racing equipment
              </p>
            </div>
            <Button onClick={() => router.visit("/garage/new")}>
              <Plus className="w-4 h-4 mr-2" />
              Add Gear
            </Button>
          </div>

          {gear_items.length === 0 ? (
            <Card className="p-12 text-center border-gray-200">
              <Settings2 className="w-12 h-12 mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                No gear yet
              </h3>
              <p className="text-gray-600 mb-6">
                Add your sim racing equipment to track usage and stats
              </p>
              <Button onClick={() => router.visit("/garage/new")}>
                <Plus className="w-4 h-4 mr-2" />
                Add Your First Gear
              </Button>
            </Card>
          ) : (
            <div className="space-y-6">
              {gear_types.map((type) => {
                const items = gearByType[type.value];
                if (!items || items.length === 0) return null;

                return (
                  <div key={type.value}>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      {GEAR_TYPE_ICONS[type.value]}
                      {type.label}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {items.map((item) => (
                        <Card
                          key={item.uuid}
                          className="p-5 border-gray-200 hover:border-gray-300 transition-colors"
                        >
                          <div className="flex justify-between items-start mb-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h4 className="font-semibold text-gray-900">
                                  {item.nickname}
                                </h4>
                                {item.is_primary && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700 border border-blue-200">
                                    Primary
                                  </span>
                                )}
                              </div>
                              {(item.brand || item.model) && (
                                <p className="text-sm text-gray-600">
                                  {item.display_name}
                                </p>
                              )}
                            </div>
                          </div>

                          <div className="flex gap-4 text-sm text-gray-600 mb-4">
                            <div>
                              <span className="font-medium">
                                {item.sessions_count}
                              </span>{" "}
                              sessions
                            </div>
                            <div>
                              <span className="font-medium">
                                {item.total_hours}h
                              </span>{" "}
                              used
                            </div>
                            <div>{formatLastUsed(item.last_used_at)}</div>
                          </div>

                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-xs"
                              onClick={() => router.visit(`/garage/${item.uuid}/edit`)}
                            >
                              <Edit className="w-3 h-3 mr-1" />
                              Edit
                            </Button>
                            {!item.is_primary && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="text-xs"
                                onClick={() => handleSetPrimary(item.uuid)}
                              >
                                Set Primary
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-xs"
                              onClick={() => handleRetire(item.uuid)}
                            >
                              <Archive className="w-3 h-3 mr-1" />
                              Retire
                            </Button>
                          </div>
                        </Card>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </main>
    </>
  );
};

GearIndex.layout = (page: React.ReactNode) => <AuthLayout>{page}</AuthLayout>;

export default GearIndex;
